import React from "react";

import {
  Button,
  FormControl,
  FormLabel,
  Typography,
  SelectInput,
  MenuItem,
  TextInput,
  FloatInput,
} from "@portex-pro/ui-components";
import FormStringConcat from "@portex-pro/ui-components/Forms/FormStringConcat";
import { UnloadingDetails } from "api/types/generated-types";
import { ShipmentPackagingType } from "app/pages/shipments/types/domain";
import { Guide } from "components/Guide";
import SingleCalendarPicker from "components/SingleCalendarPicker";
import YesNoRadio from "components/YesNoRadio";
import { DateTime } from "luxon";
import { packagingTypeList } from "pages/shipper/pages/request-quote/constants/packagingTypes";
import { getUrgencyOptions } from "pages/shipper/pages/request-quote/pages/fcl/utils/getUrgencyOptions";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { RoutingTypeEnum } from "types/RoutingType";
import { UnloadingDetailsEnum } from "types/UnloadingDetails";

import StepCollapse from "../../_components/StepCollapse";
import { useCreateStepCollapseProps } from "../../_hooks/useStepCollapse";
import { useQuoteRequestSelector } from "../../_store";
import { quoteRequestActions } from "../../_store/quoteRequestSliceV2";
import { QuoteStepIndex } from "../../types";
import { FinalSchema } from "../_schemas";

interface ShipmentDetailsStepTagProps {
  isPalletized: boolean;
  packagingType?: ShipmentPackagingType;
  palletCount?: number;
  packingCount?: number;
  isHazardous: boolean;
  cargoReadyDate?: string;
}

const ShipmentDetailsStepTag: React.FC<ShipmentDetailsStepTagProps> = ({
  isPalletized,
  packagingType,
  palletCount,
  packingCount,
  isHazardous,
  cargoReadyDate,
}) => {
  const { t } = useTranslation(["common", "quoteRequest"]);

  const parts = [];

  if (isPalletized && palletCount) {
    parts.push(`${palletCount} count of palletized boxes`);
  } else if (!isPalletized && packagingType && packingCount) {
    const packingTypeLabel = t(`quoteRequest:packagingTypes.${packagingType}`);
    parts.push(`${packingCount} ${packingTypeLabel}`);
  }

  if (isHazardous) {
    parts.push("Hazardous");
  }

  let readyDate = "";
  if (cargoReadyDate) {
    const date = DateTime.fromISO(cargoReadyDate);
    if (date.isValid) {
      readyDate = date.toFormat("MMM dd");
    }
  }

  if (parts.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center space-x-1 text-blue-600 text-sm">
      <span className="font-bold ">{parts.join(", ")}</span>
      &nbsp;{readyDate}
    </div>
  );
};

const ShipmentDetailsStep: React.FC<{ onSubmit: (data: FinalSchema) => void }> = ({ onSubmit }) => {
  const { t } = useTranslation(["common", "shipper", "quoteRequest"]);
  const [urgencyOptions] = React.useState(getUrgencyOptions());
  const { baseStepCollapseProps, setIsOpen } = useCreateStepCollapseProps({
    expectedStepIndex: QuoteStepIndex.Shipment,
    name: t("shipper:shipmentDetails"),
    number: 4,
  });

  const { quoteRequest: airQuoteRequest, stepIndex } = useQuoteRequestSelector((state) => state.quoteRequestSliceV2);
  const setQuoteRequestAction = quoteRequestActions.useSetQuoteRequest();
  const setStepIndex = quoteRequestActions.useSetStepIndex();

  const methods = useFormContext<FinalSchema>();
  const { errors } = methods.formState;

  console.log(errors);

  const isPalletized = methods.watch("isPalletized");
  const isHazardous = methods.watch("isHazardous");
  const insuranceRequired = methods.watch("insuranceRequired");
  const includeCustomsClearance = methods.watch("includeCustomsClearance");
  const cargoReadyDate = methods.watch("cargo_ready_date");

  const packagingType = useWatch({
    control: methods.control,
    name: "packagingType",
  });
  const palletCount = useWatch({
    control: methods.control,
    name: "palletCount",
  });
  const packingCount = useWatch({
    control: methods.control,
    name: "packingCount",
  });

  const submit = (data: FinalSchema) => {
    onSubmit(data);
    setStepIndex(QuoteStepIndex.Partners);
    setIsOpen(false);
  };

  return (
    <StepCollapse
      {...baseStepCollapseProps}
      tag={
        <ShipmentDetailsStepTag
          isPalletized={isPalletized || false}
          packagingType={packagingType}
          palletCount={palletCount}
          packingCount={packingCount}
          isHazardous={isHazardous || false}
          cargoReadyDate={cargoReadyDate}
        />
      }
      footer={
        stepIndex <= QuoteStepIndex.Shipment ? (
          <div className="flex justify-end mt-2 w-full">
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={methods.handleSubmit(submit)}
              style={{ width: 150 }}
            >
              {t("quoteRequest:RoutingStep.nextButton")}
            </Button>
          </div>
        ) : null
      }
    >
      <div className="space-y-8 mt-4">
        {/* Reference Number & Urgency */}
        <div className="space-y-4">
          <h3 className="text-[16px] text-gray-500 font-semibold mb-2">
            {t("quoteRequest:CargoStep.ReferenceNumberSection.title")}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <FormControl fullWidth>
                <FormLabel>
                  <span className="font-bold">PO/Reference No.</span> (optional)
                </FormLabel>
                <Controller
                  name="referenceNumber"
                  control={methods.control}
                  render={({ field }) => (
                    <TextInput
                      {...field}
                      onChange={(e) => setQuoteRequestAction({ reference_number: e.target.value })}
                      fullWidth
                      placeholder={t("quoteRequest:ShipmentDetailsStep.setReferenceNo")}
                    />
                  )}
                />
              </FormControl>
            </div>
            <div>
              <FormControl fullWidth>
                <FormLabel>{t("quoteRequest:ShipmentDetailsStep.urgency")}</FormLabel>
                <Controller
                  name="deadlineRespondAt"
                  control={methods.control}
                  render={({ field, fieldState }) => (
                    <SelectInput
                      {...field}
                      onChange={(e) => setQuoteRequestAction({ deadline_respond_at: (e.target.value as string) ?? "" })}
                      fullWidth
                      error={!!fieldState.error}
                    >
                      <MenuItem value="">Quote needed within</MenuItem>
                      {urgencyOptions.map((option) => (
                        <MenuItem key={option.id} value={option.value.toISO() ?? ""}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </SelectInput>
                  )}
                />
                {errors.deadlineRespondAt && (
                  <Typography variant="caption" color="error" style={{ marginTop: 4 }}>
                    {errors.deadlineRespondAt.message}
                  </Typography>
                )}
              </FormControl>
            </div>
          </div>
        </div>
        {/* Dates */}
        <div className="space-y-4">
          <h3 className="text-[16px] text-gray-500 font-semibold mb-2">
            {t("quoteRequest:ShipmentDetailsStep.datesLoadingDetails")}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <FormControl fullWidth>
                <FormLabel required>{t("quoteRequest:ShipmentDetailsStep.cargoReadyDate")}</FormLabel>
                <Controller
                  name="cargo_ready_date"
                  control={methods.control}
                  render={({ field }) => (
                    <SingleCalendarPicker
                      value={field.value ? DateTime.fromISO(field.value) : null}
                      onChange={(date) => setQuoteRequestAction({ load_spec: { cargo_ready_date: date?.toISO() } })}
                      startIconPallete="grey"
                      required
                      highlight={!!errors.cargo_ready_date}
                    />
                  )}
                />
                {errors.cargo_ready_date && (
                  <Typography variant="caption" color="error" style={{ marginTop: 4 }}>
                    {errors.cargo_ready_date.message}
                  </Typography>
                )}
              </FormControl>
            </div>
            <div>
              <FormControl fullWidth>
                <FormLabel>{t("quoteRequest:ShipmentDetailsStep.targetDeliveryDate")}</FormLabel>
                <Controller
                  name="target_delivery_date"
                  control={methods.control}
                  render={({ field }) => (
                    <SingleCalendarPicker
                      value={field.value ? DateTime.fromISO(field.value) : null}
                      onChange={(date) => setQuoteRequestAction({ load_spec: { target_delivery_date: date?.toISO() } })}
                      startIconPallete="grey"
                      placeholder={t("quoteRequest:ShipmentDetailsStep.selectDateOptional")}
                      clearable
                      minDate={cargoReadyDate ? DateTime.fromISO(cargoReadyDate) : undefined}
                    />
                  )}
                />
                {errors.target_delivery_date && (
                  <Typography variant="caption" color="error" style={{ marginTop: 4 }}>
                    {errors.target_delivery_date.message}
                  </Typography>
                )}
              </FormControl>
            </div>
            <div>
              {/* Unloading Details */}
              {airQuoteRequest?.routing_type !== RoutingTypeEnum.P2P &&
                airQuoteRequest?.routing_type !== RoutingTypeEnum.D2P && (
                  <div>
                    <FormControl fullWidth>
                      <FormLabel>{t("common:unloadingDetails")}</FormLabel>
                      <Controller
                        name="unloadingDetails"
                        control={methods.control}
                        render={({ field }) => (
                          <div className="flex space-x-1 p-1 rounded-lg w-full bg-gray-100">
                            <button
                              type="button"
                              onClick={() => setQuoteRequestAction({ unloading_details: UnloadingDetails.Live })}
                              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors border flex-1 ${
                                field.value === UnloadingDetailsEnum.LIVE
                                  ? "border-blue-600 text-blue-600 bg-white shadow-sm"
                                  : "border-transparent text-gray-700 hover:text-gray-900 hover:bg-gray-200"
                              }`}
                            >
                              {t("common:liveUnload")}
                            </button>
                            <button
                              type="button"
                              onClick={() => setQuoteRequestAction({ unloading_details: UnloadingDetails.Drop })}
                              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors border flex-1 ${
                                field.value === UnloadingDetailsEnum.DROP
                                  ? "border-blue-600 text-blue-600 bg-white shadow-sm"
                                  : "border-transparent text-gray-700 hover:text-gray-900 hover:bg-gray-200"
                              }`}
                            >
                              {t("common:dropAndPick")}
                            </button>
                          </div>
                        )}
                      />
                    </FormControl>
                  </div>
                )}
            </div>
            <div></div>
          </div>
        </div>

        {/* Containers */}
        <div className="space-y-4">
          <h3 className="text-[16px] text-gray-500 font-semibold">{t("common:containers")}</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <FormControl margin="dense" fullWidth>
                <FormLabel>{t("quoteRequest:ShipmentDetailsStep.packingMethod")}</FormLabel>
                <Controller
                  name="isPalletized"
                  control={methods.control}
                  render={({ field }) => (
                    <div className="flex space-x-1 p-1 rounded-lg w-full bg-gray-100">
                      <button
                        type="button"
                        onClick={() => setQuoteRequestAction({ load_spec: { is_palletized: true } })}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors border flex-1 ${
                          field.value
                            ? "border-blue-600 text-blue-600 bg-white shadow-sm"
                            : "border-transparent text-gray-700 hover:text-gray-900 hover:bg-gray-200"
                        }`}
                      >
                        {t("quoteRequest:ShipmentDetailsStep.palletized")}
                      </button>
                      <button
                        type="button"
                        onClick={() => setQuoteRequestAction({ load_spec: { is_palletized: false } })}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors border flex-1 ${
                          !field.value
                            ? "border-blue-600 text-blue-600 bg-white shadow-sm"
                            : "border-transparent text-gray-700 hover:text-gray-900 hover:bg-gray-200"
                        }`}
                      >
                        {t("quoteRequest:ShipmentDetailsStep.floorLoaded")}
                      </button>
                    </div>
                  )}
                />
              </FormControl>
            </div>

            {isPalletized ? (
              <div>
                <FormControl margin="dense" fullWidth>
                  <FormLabel required>{t("quoteRequest:ShipmentDetailsStep.palletCount")}</FormLabel>
                  <Controller
                    name="palletCount"
                    control={methods.control}
                    render={({ field }) => (
                      <TextInput
                        {...field}
                        type="number"
                        inputProps={{ min: 0 }}
                        fullWidth
                        placeholder={t("quoteRequest:ShipmentDetailsStep.setPalletCount")}
                        onChange={(e) => {
                          const value = parseInt(e.target.value, 10);
                          setQuoteRequestAction({ load_spec: { pallet_count: value } });
                        }}
                        error={!!errors.palletCount}
                      />
                    )}
                  />
                  {errors.palletCount && (
                    <Typography variant="caption" color="error" style={{ marginTop: 4 }}>
                      {errors.palletCount.message}
                    </Typography>
                  )}
                </FormControl>
              </div>
            ) : (
              <>
                <div>
                  <FormControl margin="dense" fullWidth>
                    <FormLabel required>{t("quoteRequest:ShipmentDetailsStep.packingType")}</FormLabel>
                    <Controller
                      name="packagingType"
                      control={methods.control}
                      rules={{
                        validate: (value) => {
                          if (!isPalletized && !value) {
                            return t("quoteRequest:ShipmentDetailsStep.packingTypeRequired");
                          }
                          return true;
                        },
                      }}
                      render={({ field }) => (
                        <SelectInput
                          {...field}
                          onChange={(e) =>
                            setQuoteRequestAction({
                              load_spec: { packaging_type: e.target.value as ShipmentPackagingType },
                            })
                          }
                          fullWidth
                          error={!!errors.packagingType}
                        >
                          <MenuItem value="">{t("quoteRequest:ShipmentDetailsStep.setPackingType")}</MenuItem>
                          {packagingTypeList.map((type) => (
                            <MenuItem key={type} value={type}>
                              {t(`quoteRequest:packagingTypes.${type}`)}
                            </MenuItem>
                          ))}
                        </SelectInput>
                      )}
                    />
                    {errors.packagingType && (
                      <Typography variant="caption" color="error" style={{ marginTop: 4 }}>
                        {errors.packagingType.message}
                      </Typography>
                    )}
                  </FormControl>
                </div>
                <div>
                  <FormControl margin="dense" fullWidth>
                    <FormLabel required>{t("quoteRequest:ShipmentDetailsStep.packingCount")}</FormLabel>
                    <Controller
                      name="packingCount"
                      control={methods.control}
                      rules={{
                        validate: (value) => {
                          if (!isPalletized && (!value || value <= 0)) {
                            return t("quoteRequest:ShipmentDetailsStep.packingCountRequired");
                          }
                          return true;
                        },
                      }}
                      render={({ field }) => (
                        <TextInput
                          {...field}
                          type="number"
                          inputProps={{ min: 0 }}
                          fullWidth
                          placeholder={t("quoteRequest:ShipmentDetailsStep.setPackingCount")}
                          onChange={(e) => {
                            const value = parseInt(e.target.value, 10);
                            setQuoteRequestAction({ load_spec: { packing_count: value } });
                          }}
                          error={!!errors.packingCount}
                          required={!isPalletized}
                        />
                      )}
                    />
                    {errors.packingCount && (
                      <Typography variant="caption" color="error" style={{ marginTop: 4 }}>
                        {errors.packingCount.message}
                      </Typography>
                    )}
                  </FormControl>
                </div>
              </>
            )}

            <div>
              <FormControl margin="dense" fullWidth>
                <FormLabel>{t("quoteRequest:ShipmentDetailsStep.overweightLabel")}</FormLabel>
                <Controller
                  name="isOverweight"
                  control={methods.control}
                  render={({ field }) => (
                    <YesNoRadio
                      value={field.value}
                      onChange={(value) => setQuoteRequestAction({ load_spec: { is_overweight: value } })}
                    />
                  )}
                />
              </FormControl>
            </div>
          </div>
        </div>

        {/* Commodities */}
        <div className="space-y-4">
          <h3 className="text-[16px] text-gray-500 font-semibold">{t("common:commodities")}</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <FormControl margin="dense" fullWidth>
                <FormLabel required={includeCustomsClearance}>{t("common:commodities")}</FormLabel>
                <Controller
                  name="commodities"
                  control={methods.control}
                  render={({ field, fieldState }) => (
                    <Guide showGuide={!field.value && !fieldState.error && !!includeCustomsClearance}>
                      <FormStringConcat
                        margin="dense"
                        value={field.value || ""}
                        onChange={(value) => setQuoteRequestAction({ load_spec: { commodities: value } })}
                        highlight={!!errors.commodities}
                        TextInputProps={{
                          required: includeCustomsClearance,
                          error: !!errors.commodities,
                          placeholder: t("common:commodities"),
                        }}
                      />
                    </Guide>
                  )}
                />
                {errors.commodities && (
                  <Typography variant="caption" color="error" style={{ marginTop: 4 }}>
                    {errors.commodities.message}
                  </Typography>
                )}
              </FormControl>
            </div>
            <div>
              <FormControl margin="dense" fullWidth>
                <FormLabel>{t("quoteRequest:ShipmentDetailsStep.hazardousLabel")}</FormLabel>
                <Controller
                  name="isHazardous"
                  control={methods.control}
                  render={({ field }) => (
                    <YesNoRadio
                      value={field.value}
                      onChange={(value) => setQuoteRequestAction({ is_hazardous: value })}
                    />
                  )}
                />
              </FormControl>
            </div>
            <div>
              <FormControl margin="dense" fullWidth>
                <FormLabel>{t("quoteRequest:ShipmentDetailsStep.insuranceLabel")}</FormLabel>
                <Controller
                  name="insuranceRequired"
                  control={methods.control}
                  render={({ field }) => (
                    <YesNoRadio
                      value={field.value}
                      onChange={(value) => setQuoteRequestAction({ insurance_required: value })}
                    />
                  )}
                />
              </FormControl>
            </div>
            <div>
              <FormControl margin="dense" fullWidth>
                <FormLabel>{t("quoteRequest:ShipmentDetailsStep.customsLabel")}</FormLabel>
                <Controller
                  name="includeCustomsClearance"
                  control={methods.control}
                  render={({ field }) => (
                    <YesNoRadio
                      value={field.value}
                      onChange={(value) => setQuoteRequestAction({ load_spec: { include_customs_clearance: value } })}
                    />
                  )}
                />
              </FormControl>
            </div>
          </div>

          {isHazardous && (
            <div>
              <FormControl margin="dense" fullWidth>
                <FormLabel required>{t("quoteRequest:ShipmentDetailsStep.hazardousDetailsLabel")}</FormLabel>
                <Controller
                  name="hazardousGoodsDetails"
                  control={methods.control}
                  rules={{
                    validate: (value) => {
                      if (isHazardous && !value) {
                        return t("quoteRequest:ShipmentDetailsStep.hazardousDetailsRequired");
                      }
                      return true;
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <Guide showGuide={!field.value && !fieldState.error}>
                      <TextInput
                        {...field}
                        onChange={(e) => setQuoteRequestAction({ hazardous_goods_details: e.target.value })}
                        multiline
                        rows={3}
                        fullWidth
                        placeholder={t("quoteRequest:ShipmentDetailsStep.hazardousDetailsPlaceholder")}
                        error={!!errors.hazardousGoodsDetails}
                        required={isHazardous}
                      />
                    </Guide>
                  )}
                />
                {errors.hazardousGoodsDetails && (
                  <Typography variant="caption" color="error" style={{ marginTop: 4 }}>
                    {errors.hazardousGoodsDetails.message}
                  </Typography>
                )}
              </FormControl>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {insuranceRequired && (
            <div className="col-span-2">
              <FormControl margin="dense" fullWidth>
                <FormLabel>{t("common:goodsValueOptional")}</FormLabel>
                <Controller
                  name="goodsValue"
                  control={methods.control}
                  render={({ field }) => (
                    <FloatInput
                      {...field}
                      onChange={(value) => setQuoteRequestAction({ goods_value: value })}
                      startIcon={<div className="ml-4">$</div>}
                      fullWidth
                      placeholder={t("common:goodsValue")}
                      decimalPlace={2}
                    />
                  )}
                />
              </FormControl>
            </div>
          )}
          {/* Additional Notes */}
          <div className="col-span-3">
            <FormControl margin="dense" fullWidth>
              <FormLabel>{t("common:additionalNotes")}</FormLabel>
              <Controller
                name="additionalNotes"
                control={methods.control}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    onChange={(e) => setQuoteRequestAction({ note: e.target.value })}
                    multiline
                    rows={3}
                    fullWidth
                    placeholder={t("quoteRequest:ShipmentDetailsStep.additionalNotesPlaceholder")}
                  />
                )}
              />
            </FormControl>
          </div>
        </div>
      </div>
    </StepCollapse>
  );
};

export default ShipmentDetailsStep;
